import { useState, useEffect } from 'react';
import { Text, Title, Group, Badge, Button, Image, Stack, Divider, Spoiler, Box, Flex, Avatar } from '@mantine/core';
import { IconCalendar, IconMapPin, IconClock, IconTicket, IconExternalLink } from '@tabler/icons-react';

import '../../styles/ShowView.css';

interface Artist {
  id: number;
  name: string;
  slug: string;
  description?: any; // Changed to any to account for complex description object
  avatar_url?: string;
  instructor?: boolean;
  dj?: boolean;
  dance_style_ids: number[];
  dance_styles: { id: number; name: string }[];
}

interface ArtistDrawerContentProps {
  artist: Artist;
}

export default function ArtistDrawerContent({ artist }: ArtistDrawerContentProps) {
  useEffect(() => {
    console.log('Artist received in drawer:', artist);
  }, [artist]);

  // Check if artist is a PointerEvent or other non-Artist object
  if (!artist || typeof artist !== 'object' || 'isTrusted' in artist) {
    return <Text>Loading artist information...</Text>;
  }

  return (
    <Stack gap="xs" color="gray" style={{ width: '100%', minHeight: '100vh', height: 'auto', paddingBottom: '10rem' }}>
      {/* Display artist image */}
      {artist.avatar_url && (
        <Box>
          <Image
            src={artist.avatar_url}
            alt={artist.name || 'Artist'}
            radius="md"
            fit="contain"
            style={{ width: '100%' }}
          />
        </Box>
      )}
      <Text fw={700} size="32px" style={{ lineHeight: 1 }}>
        {artist?.name || 'Unknown Artist'}
      </Text>

      {/* Display artist role badges */}
      <Group gap="xs" style={{ margin: '0rem', padding: '0rem' }}>
        {artist.instructor && (
          <Badge
            color="#EA580C"
            variant="default"
            size="lg"
            style={{
              textTransform: 'none',
            }}
          >
            Instructor
          </Badge>
        )}
        {artist.dj && (
          <Badge color="green" variant="default" size="lg" style={{ textTransform: 'none' }}>
            DJ
          </Badge>
        )}
      </Group>

      {artist.dance_styles && artist.dance_styles.length > 0 && (
        <>
          <div style={{ marginBottom: '-20px', marginTop: '0px' }}>
            <Text size="lg" fw={600} style={{ color: '#f2f2f2', lineHeight: 1.5 }}>
              Dance Styles
            </Text>
          </div>
          <Divider color="#757575" mt={8} mb={8} />

          <Group gap="xs" mb={16}>
            {artist.dance_styles.map((style: { id: number; name: string }) => (
              <Badge
                key={style.id}
                color="#EA580C"
                size="lg"
                variant="filled"
                style={{ backgroundColor: '#EA580C', textTransform: 'none' }}
                className="poppins"
              >
                {style.name}
              </Badge>
            ))}
          </Group>
        </>
      )}

      {/* Description */}
      {artist.description && (
        <Box mt="0">
          <div style={{ marginBottom: '0px', marginTop: '0px' }}>
            <Text size="lg" fw={600} style={{ color: '#f2f2f2', lineHeight: 1.5 }}>
              About
            </Text>
          </div>
          <Divider color="#757575" mt={0} mb={16} />
          <Spoiler
            maxHeight={150}
            mt="0"
            showLabel="Read more"
            hideLabel="Show less"
            color="orange"
            classNames={{
              control: 'custom-spoiler-control',
            }}
          >
            <Text
              mt="xs"
              dangerouslySetInnerHTML={{ __html: artist.description }}
              style={{ color: '#FFF7ED' }}
              className="event-description"
            />
          </Spoiler>
        </Box>
      )}
    </Stack>
  );
}
